import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'

interface SpaceReservationDetail {
  reservation_id: number
  space_id: number
  space_name: string
  reserve_date: string
  time_slot_id: number
  start_time: string | null
  end_time: string | null
  label: string | null
  applicant_name: string | null
  phone: string | null
  guest_org: string | null
  headcount: number
  status: number
  created_at: string
}

const STATUS_STYLE: Record<number, { label: string; cls: string }> = {
  0: { label: '확인대기', cls: 'bg-[#fff8e1] text-[#e67e22]' },
  1: { label: '확인완료', cls: 'bg-[#e8f5e9] text-[#388e3c]' },
  2: { label: '신청취소', cls: 'bg-[#f5f5f5] text-[#9e9e9e]' },
}

export default function AdminSpaceReservationDetail() {
  const { reservation_id } = useParams<{ reservation_id: string }>()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<SpaceReservationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    fetchDetail()
  }, [reservation_id])

  const fetchDetail = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/reservations/${reservation_id}`))
      if (!res.ok) throw new Error('조회 실패')
      const data: SpaceReservationDetail = await res.json()
      setDetail(data)
    } catch {
      alert('예약 정보를 불러오는 데 실패했습니다.')
      navigate('/admin/space?tab=reservations')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!window.confirm('예약을 확인(승인) 처리하시겠습니까?')) return
    setActionLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/reservations/${reservation_id}/approve`), {
        method: 'POST',
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchDetail()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm('예약을 취소 처리하시겠습니까?')) return
    setActionLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/reservations/${reservation_id}/cancel`), {
        method: 'POST',
      })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchDetail()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const formatDateTime = (d: string) =>
    new Date(d).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })

  const formatTime = (t: string | null) => (t ? t.slice(0, 5) : '')

  if (loading) {
    return (
      <div className="bg-[#f5f2f0] min-h-screen p-8 flex items-center justify-center">
        <p className="text-gray-300 text-[13px]">불러오는 중...</p>
      </div>
    )
  }

  if (!detail) return null

  const st = STATUS_STYLE[detail.status] ?? { label: String(detail.status), cls: 'bg-gray-100 text-gray-500' }
  const timeLabel = detail.label
    ?? (detail.start_time && detail.end_time
      ? `${formatTime(detail.start_time)} ~ ${formatTime(detail.end_time)}`
      : '-')

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">공간 예약 상세</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/space?tab=reservations')}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            ← 목록으로
          </button>
        </div>

        {/* Status Badge */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-4 mb-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-gray-400">예약 #{detail.reservation_id}</span>
            <span className={`text-[12px] px-2.5 py-1 rounded-lg font-semibold ${st.cls}`}>{st.label}</span>
          </div>
          <span className="text-[12px] text-gray-400">신청일: {formatDateTime(detail.created_at)}</span>
        </div>

        {/* Detail Card */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">

          <SectionTitle>공간 정보</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-6">
            <InfoRow label="공간명" value={detail.space_name} />
            <InfoRow label="예약 날짜" value={formatDate(detail.reserve_date)} />
            <InfoRow label="예약 시간" value={timeLabel} />
            <InfoRow label="예약 인원" value={`${detail.headcount}명`} />
          </div>

          <div className="border-t border-[#f5f2f0] my-4" />
          <SectionTitle>신청자 정보</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <InfoRow label="이름" value={detail.applicant_name ?? '-'} />
            <InfoRow label="전화번호" value={detail.phone ?? '-'} />
            {detail.guest_org && (
              <InfoRow label="단체명" value={detail.guest_org} />
            )}
          </div>

          {/* Actions */}
          {detail.status === 0 && (
            <>
              <div className="border-t border-[#f5f2f0] mt-6 pt-5 flex justify-end gap-2">
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="text-[13px] text-red-400 font-semibold px-5 py-2.5 rounded-lg border border-red-100 bg-white cursor-pointer hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  취소 처리
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50"
                >
                  {actionLoading ? '처리 중...' : '확인 처리'}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[13px] font-bold text-[#5a3d32]">{children}</h2>
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-[#5a3d32] font-medium">{label}</span>
      <span className="text-[13px] text-[#2a1f1a]">{value}</span>
    </div>
  )
}